import React, { useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import TasksPage from './pages/TasksPage';
import ArchivesPage from './pages/ArchivesPage';
import ClientsPage from './pages/ClientsPage';
import DepotsPage from './pages/DepotsPage';
import ProfilePage from './pages/ProfilePage';
import DriverPage from './pages/DriverPage';
import UsersPage from './pages/UsersPage';
import DumpstersPage from './pages/DumpstersPage';
import MapPage from './pages/MapPage';
import PositionsPage from './pages/PositionsPage';
import LogsPage from './pages/LogsPage';
import AppVersionsPage from './pages/AppVersionsPage';
import useAppStore from './store';
import { supabase } from './lib/supabase';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loadInitialData, currentUser, logout } = useAppStore();
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        // If already authenticated and has current user, no need to check further
        if (isAuthenticated && currentUser) {
          // Update last_active timestamp periodically to maintain session
          const updateLastActive = async () => {
            try {
              await supabase
                .from('users')
                .update({ last_active: new Date().toISOString() })
                .eq('id', currentUser.id);
            } catch (error) {
              console.error('Error updating last_active:', error);
            }
          };
          
          // Update immediately and then every 5 minutes
          updateLastActive();
          intervalIdRef.current = setInterval(updateLastActive, 5 * 60 * 1000);
          
          return;
        }

        // Get the session from Supabase
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        // Handle session check error
        if (sessionError) {
          console.error('Session check error:', sessionError.message);
          await handleLogout();
          return;
        }

        // If no session and not on login page, redirect to login
        if (!session) {
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
          return;
        }

        // Check if user exists in the database
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (userError) {
          console.error('User check error:', userError.message);
          await handleLogout();
          return;
        }

        // If no user data found, handle as a new login
        if (!userData) {
          try {
            await loadInitialData();
            if (location.pathname === '/login') {
              navigate('/', { replace: true });
            }
            return;
          } catch (error) {
            console.error('Failed to load initial data:', error);
            await handleLogout();
            return;
          }
        }

        // If user is already logged in elsewhere (different user ID from current session)
        if (userData.is_online && session.user.id !== userData.id) {
          alert('Votre compte est déjà connecté sur un autre appareil');
          await handleLogout();
          return;
        }

        // Only try to load initial data if we have a session
        if (session) {
          try {
            await loadInitialData();
            
            // If we're on the login page with a valid session, redirect to home
            if (location.pathname === '/login') {
              navigate('/', { replace: true });
            }
            return;
          } catch (loadError) {
            // Only log the error and handle logout if it's not a "No active session" error
            if (loadError instanceof Error && loadError.message !== 'No active session') {
              console.error('Failed to load initial data:', loadError);
              await handleLogout();
              return;
            } else {
              // If there's no active session, just redirect to login
              if (location.pathname !== '/login') {
                navigate('/login', { replace: true });
              }
              return;
            }
          }
        }
        
        return;
      } catch (error) {
        console.error('Auth check error:', error);
        await handleLogout();
        return;
      }
    };

    const handleLogout = async () => {
      try {
        await logout();
      } catch (error) {
        console.error('Sign out error:', error);
      } finally {
        localStorage.clear();
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
      }
    };

    checkAuth();

    // Cleanup function for useEffect
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [navigate, location.pathname, isAuthenticated, loadInitialData, currentUser, logout]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="map" element={<MapPage />} />
        <Route path="positions" element={<PositionsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="archives" element={<ArchivesPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="depots" element={<DepotsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="driver" element={<DriverPage />} />
        <Route path="dumpsters" element={<DumpstersPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="app-versions" element={<AppVersionsPage />} />
      </Route>
    </Routes>
  );
};

export default App;